import cn from 'classnames';
import styles from './Input.module.scss';
import { ReactNode, useRef, useState } from 'react';
import { FieldError, useController, useFormContext } from 'react-hook-form';
import { formatPhone } from '@/utils/formatPhone';

type InputProps = {
  label?: string;
  placeholder: string;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern: RegExp;
  type?: string;
  name: string;
  className?: string;
  disabled?: boolean;
  readonly?: boolean;
  description?: string;
  content?: ReactNode;
  onChange?: (value: string) => void;
};

export function Input({
  label,
  placeholder,
  required = false,
  maxLength = 100,
  minLength = 0,
  pattern,
  type = 'text',
  name,
  className,
  disabled,
  readonly,
  description,
  content,
  onChange,
}: InputProps) {
  const [showPassword, setShowPassword] = useState(false);
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    rules: {
      required: { value: required, message: 'required' },
      maxLength: { value: maxLength, message: 'maxLength' },
      minLength: { value: minLength, message: 'minLength' },
      pattern: { value: pattern, message: 'pattern' },
    },
    defaultValue: '',
    control,
  });

  const wrapperClassName = cn(styles.wrapper, className);
  const inputClassName = cn(styles.container, {
    [styles.error]: fieldState.error,
    [styles.disabled]: disabled,
    [styles.readonly]: readonly,
  });

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = event.target.value;

    if (maxLength && inputValue.length > maxLength) {
      inputValue = inputValue.slice(0, maxLength);
      return;
    }

    if (type === 'phone') {
      const formattedValue = formatPhone(inputValue);
      field.onChange(formattedValue);
      return;
    }

    field.onChange(event);
    onChange?.(inputValue);
  };

  const handleContainerClick = () => {
    if (!inputRef.current) return;
    if (disabled || readonly) return;

    if (type === 'date') {
      inputRef.current.showPicker();
      return;
    }

    inputRef.current.focus();
  };

  return (
    <div className={wrapperClassName} onClick={handleContainerClick}>
      {label && <p className={styles.label}>{label}</p>}
      {content && <div className={styles.content}>{content}</div>}
      <div className={inputClassName}>
        {type === 'phone' && <div className={styles.prefix}>+7</div>}
        <input
          {...field}
          ref={inputRef}
          onChange={handleChange}
          onBlur={field.onBlur}
          placeholder={placeholder}
          type={
            type === 'password' ? (showPassword ? 'text' : 'password') : type
          }
          className={styles.input}
        />
        {type === 'password' && (
          <img
            src="/svg/eye.svg"
            alt="eye"
            className={styles.eye}
            onClick={(event) => {
              event.stopPropagation();
              setShowPassword(!showPassword);
            }}
          />
        )}
      </div>
      {fieldState.error ? (
        <p className={styles.errorTag}>
          {handleError(type, fieldState.error, minLength, maxLength)}
        </p>
      ) : (
        <BottomLabel description={description} />
      )}
    </div>
  );
}

function BottomLabel({ description }: { description?: string }) {
  if (!description) return null;
  return <p className={styles.description}>{description}</p>;
}

function handleError(
  type: string,
  error: FieldError,
  minLength?: number,
  maxLength?: number,
) {
  switch (error.type) {
    case 'required':
      return '*обязательное поле';
    case 'pattern':
      if (type === 'password')
        return 'Пароль должен содержать цифры, латинские буквы и быть не менее 8 символов';
      if (type === 'e-mail') return 'Неверный формат email';
      if (type === 'tel') return 'Неверный формат номера телефона';
      if (type === 'date') return 'Неверный формат даты';
      return 'Неверный формат поля';
    case 'exists':
      return 'Пользователь с таким email уже существует';
    case 'notFound':
      if (type === 'e-mail') return 'Пользователь с таким email не найден';
      break;
    case 'minLength':
      return `Минимальная длина ${minLength} символов`;
    case 'maxLength':
      return `Максимальная длина ${maxLength} символов`;
    case 'validate':
      return '';
    case 'mismatch':
      return 'Пароли не совпадают';
    case 'birthday':
      return 'Дата рождения не может быть позже сегодняшней';
    default:
      return 'Ошибка при заполнении поля';
  }
}
